import type { Shadows } from '@mui/material/styles'
import { createTheme } from '@mui/material/styles'

const PRIMARY = '#0f4c81'
const PRIMARY_2 = '#1f6fb2'

const shadows: Shadows = [
  'none',
  '0 10px 24px rgba(15, 76, 129, 0.08)',
  '0 10px 24px rgba(15, 76, 129, 0.08)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
  '0 10px 24px rgba(15, 76, 129, 0.10)',
]

export const theme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: '#0a3560', light: PRIMARY_2 },
    secondary: { main: PRIMARY_2 },
    success: { main: '#198754' },
    warning: { main: '#b7791f' },
    error: { main: '#b42318' },
    background: {
      default: '#f4f7fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280',
    },
    divider: '#d8e0ea',
  },
  typography: {
    fontFamily: 'Arial, Helvetica, sans-serif',
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 700, color: PRIMARY },
    body2: { fontSize: '0.875rem' },
  },
  shape: { borderRadius: 16 },
  shadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#f4f7fb' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '1px solid #d8e0ea',
          boxShadow: '0 10px 24px rgba(15, 76, 129, 0.08)',
        },
        outlined: {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #d8e0ea',
          boxShadow: '0 10px 24px rgba(15, 76, 129, 0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.875rem',
        },
        containedPrimary: {
          background: PRIMARY,
          '&:hover': { background: '#0a3560' },
        },
        outlinedPrimary: {
          borderColor: '#b9d5ec',
          color: PRIMARY,
          '&:hover': { background: '#eaf3fb', borderColor: '#7ab4e3' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: '0.875rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#7ab4e3',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#7ab4e3',
            boxShadow: '0 0 0 3px rgba(31, 111, 178, 0.12)',
          },
        },
        notchedOutline: {
          borderColor: '#d8e0ea',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.875rem', fontWeight: 700 },
        asterisk: {
          color: '#b42318',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: '#b42318',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#f8fafc',
            color: '#6b7280',
            fontSize: '0.8125rem',
            fontWeight: 700,
            borderBottom: '1px solid #d8e0ea',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderBottom: '1px solid #d8e0ea',
          padding: '12px',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: '1px solid #d8e0ea',
          boxShadow: '0 10px 24px rgba(15, 76, 129, 0.08)',
          overflow: 'hidden',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
          fontSize: '0.75rem',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { fontSize: '0.875rem' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#d8e0ea' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, fontSize: '0.875rem' },
      },
    },
    MuiSnackbar: {
      defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'center' } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: '0.75rem' },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          border: '1px solid #d8e0ea',
          borderRadius: 12,
          marginBottom: 8,
          background: '#ffffff',
        },
      },
    },
  },
})
